/*
 * Small helpers shared by the account forms: a login proposed from the
 * names, a generated password, and a strength rating. No service import,
 * so any screen can use them without dragging the HTTP layer along.
 */

/** A login proposed from the names: "julie.martin", ASCII only, no accents. */
export const suggestUsername = (firstName: string, lastName: string): string => {
  const clean = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  const parts = [clean(firstName), clean(lastName)].filter(Boolean);
  return parts.join('.');
};

/**
 * A 12-character password: at least one lowercase, one uppercase, one digit
 * and two symbols, the rest drawn from all three sets, then shuffled. No
 * look-alike characters (0/O, 1/l/I), so it survives being read out.
 */
export const generatePassword = (): string => {
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '23456789';
  const symbols = '!#%+-=?';
  const all = lower + upper + digits;
  const random = (n: number): number[] => {
    const values = new Uint32Array(n);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(values);
    else for (let i = 0; i < n; i += 1) values[i] = Math.floor(Math.random() * 4294967296);
    return Array.from(values);
  };
  const pick = (set: string, n: number) => random(n).map((v) => set[v % set.length]);
  const chars = [...pick(lower, 1), ...pick(upper, 1), ...pick(digits, 1), ...pick(symbols, 2), ...pick(all, 7)];
  // Fisher-Yates, so the guaranteed characters do not sit at fixed positions.
  const r = random(chars.length);
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = r[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};

/** 0 (empty) to 4 (strong): length, then variety. */
export const passwordStrength = (password: string): number => {
  if (!password) return 0;
  let score = password.length >= 8 ? 1 : 0;
  if (password.length >= 12) score += 1;
  const kinds = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((re) => re.test(password)).length;
  if (kinds >= 3) score += 1;
  if (kinds === 4 && password.length >= 12) score += 1;
  return Math.min(4, score);
};
